/**
 * DI·`@sbe-spinoff-client/staff-http` 의존 없는 순수 함수만 모은다 — 이 lib을 소비하는
 * 다른 워크스페이스 lib(`workspace:*`)의 유닛 테스트 번들러(Vite)가 lib→lib bare
 * specifier를 해석하지 못하는 문제를 피하기 위해 분리했다. `staff-oidc-auth.service.ts`가
 * 이 함수들을 감싸 DI 컨텍스트를 붙인다.
 *
 * `buildAuthorizeRedirectUrl`의 trailing-slash 정규화는 `@sbe-spinoff-client/staff-http`의
 * `normalizeBaseUrl`과 로직이 같지만 의도적으로 재사용하지 않는다 — import하는 순간
 * 이 파일 전체가 위에서 말한 cross-lib 해석 문제를 다시 겪어 이 파일을 분리한 이유
 * 자체가 무효화된다. 1줄짜리 정규화라 두 lib이 각자 갖는 비용이 낮다고 판단했다.
 */

/** return 경로가 비어있거나 `/`일 때 `fallback`으로 치환한다. `fallback`이 없으면 그대로 둔다. */
export function resolveReturnPath(
  returnPath: string,
  fallback: string,
): string {
  if (!fallback) return returnPath;
  return returnPath && returnPath !== '/' ? returnPath : fallback;
}

/** provider 슬러그 기본값 — 학교종이 OP. 클라이언트 lib 은 백엔드 계약 lib 을 import 하지 못해 리터럴이고, `PROVIDER_SLUGS` 와의 일치는 계약 lib 의 정합 검사가 지킨다. */
export const DEFAULT_STAFF_PROVIDER = 'schoolbell-e';

/**
 * `<baseUrl>/staff/auth/sso/<provider>/authorize-redirect?return=<returnPath>` 를 조립한다.
 * `baseUrl`이 상대 경로(예: `/api`)면 `new URL()`이 base 인자 없이는 던지므로,
 * 브라우저 origin을 base로 준다(호출부가 브라우저 컨텍스트를 보장하지만, 이 함수
 * 자체는 순수 함수로 독립 호출될 수 있어 방어적으로 처리한다).
 */
export function buildAuthorizeRedirectUrl(
  baseUrl: string,
  returnPath: string,
  provider: string = DEFAULT_STAFF_PROVIDER,
): string {
  const apiOrigin = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  const base =
    typeof window !== 'undefined' ? window.location.origin : undefined;
  const url = new URL(
    `${apiOrigin}/staff/auth/sso/${encodeURIComponent(provider)}/authorize-redirect`,
    base,
  );
  url.searchParams.set('return', returnPath);
  return url.toString();
}

/**
 * `url`의 path만으로 `/staff/auth/*` 자체 호출인지 판별한다 — 단순 `includes()`는
 * 무관한 요청의 쿼리 파라미터 값에 우연히 이 문자열이 들어 있으면 오탐할 수 있다
 * (`URL.pathname`은 쿼리를 포함하지 않는다).
 */
export function isOidcStaffAuthRoute(url: string): boolean {
  return new URL(url, window.location.origin).pathname.startsWith(
    '/staff/auth/',
  );
}

/**
 * Bearer 를 붙이지 않고 401 재시도도 하지 않는 `/staff/auth/*` 호출인지 판별한다 —
 * refresh 쿠키만 쓰는 `refresh`·`signout`, 아직 토큰이 없는 시점의 자체 로그인 `login`(비밀번호 오류의 401 이 불필요한 refresh 를 일으키지 않게), 브라우저 이동용 `<provider>/authorize-redirect`.
 * `me` 는 Bearer 가 필요하므로 여기 포함하지 않는다.
 */
export function isStaffTokenlessAuthRoute(url: string): boolean {
  const { pathname } = new URL(url, window.location.origin);
  return (
    pathname === '/staff/auth/refresh' ||
    pathname === '/staff/auth/signout' ||
    pathname === '/staff/auth/login' ||
    /^\/staff\/auth\/sso\/[^/]+\/authorize-redirect$/.test(pathname)
  );
}
